import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { getSeekerProfile } from './seekerProfileService';
import { getJobById } from './jobService';
import { getUserActiveSubscription, updateUsageStats } from './subscriptionService';
import { sendPersonalNotification } from './fcmService';
import { getCompanyProfile } from './companyService';

const COLLECTIONS = {
  APPLICATIONS: 'applications'
};

export const APPLICATION_STATUS = {
  PENDING: 'pending',
  REVIEWING: 'reviewing',
  SHORTLISTED: 'shortlisted',
  INTERVIEWED: 'interviewed',
  REJECTED: 'rejected',
  ACCEPTED: 'accepted',
  WITHDRAWN: 'withdrawn'
};

/**
 * Apply for a job
 * @param {string} jobId - Job ID
 * @param {string} applicantId - User ID of applicant
 * @param {Object} applicationData - Additional application data
 * @returns {Object} Result object
 */
export const applyForJob = async (jobId, applicantId, applicationData = {}) => {
  try {
    // Check if user has already applied for this job
    const existingApplication = await checkExistingApplication(jobId, applicantId);
    if (existingApplication.exists) {
      return {
        success: false,
        error: 'You have already applied for this job',
        applicationId: existingApplication.applicationId
      };
    }

    // Get job details
    const jobResult = await getJobById(jobId);
    if (!jobResult.success) {
      return {
        success: false,
        error: 'Job not found'
      };
    }

    // Get seeker profile
    const profileResult = await getSeekerProfile(applicantId);
    if (!profileResult.success) {
      return {
        success: false,
        error: 'Please complete your profile before applying'
      };
    }

    const job = jobResult.job;
    const profile = profileResult.profile;

    // Check subscription limits
    console.log('📊 [applyForJob] Checking subscription limits for applicant:', applicantId);
    const subscriptionResult = await getUserActiveSubscription(applicantId);

    if (!subscriptionResult.success || !subscriptionResult.subscription) {
      console.warn('⚠️ [applyForJob] No active subscription found for applicant');
      return {
        success: false,
        error: 'You need an active subscription package to apply for jobs. Please purchase a package.'
      };
    }

    const subscription = subscriptionResult.subscription;
    const maxApplications = subscription.maxApplications || 0;
    const applicationsUsed = subscription.usageStats?.applicationsUsed || 0;

    console.log(`📊 [applyForJob] Subscription check: used ${applicationsUsed}/${maxApplications}`);

    if (applicationsUsed >= maxApplications) {
      console.warn('⚠️ [applyForJob] Application limit reached');
      return {
        success: false,
        error: 'You have reached your application limit. Please upgrade your package to apply for more jobs.'
      };
    }

    // Get company details
    let companyDetails = null;
    try {
      const companyResult = await getCompanyProfile(job.userId);
      if (companyResult.success && companyResult.data) {
        const company = companyResult.data;
        companyDetails = {
          companyName: company.companyName || '',
          email: company.email || '',
          phone: company.phone || '',
          website: company.website || '',
          address: company.address || '',
          city: company.city || '',
          state: company.state || '',
          country: company.country || '',
          zipCode: company.zipCode || '',
          industry: company.industry || '',
          companySize: company.companySize || '',
          logoUrl: company.logoUrl || ''
        };
      }
    } catch (companyError) {
      console.warn('Could not fetch company details:', companyError);
    }

    // Create application document
    const applicationDoc = {
      jobId,
      applicantId,
      employerId: job.userId,
      companyId: job.userId, // Use job.userId as companyId since companies are stored by user ID
      jobTitle: job.title,
      companyName: job.company, // Use job.company instead of job.companyName
      applicantName: `${profile.personalInfo.firstName} ${profile.personalInfo.lastName}`,
      applicantEmail: profile.personalInfo.email,
      applicantPhone: profile.personalInfo.phone,
      applicantLocation: profile.personalInfo.location,
      resumeURL: profile.resumeURL,
      coverLetter: applicationData.coverLetter || '',
      status: APPLICATION_STATUS.PENDING,
      appliedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Store key profile data for quick access
      applicantProfile: {
        skills: profile.skills || [],
        experience: profile.experience || [],
        education: profile.education || [],
        professionalSummary: profile.professionalSummary || '',
        expectedSalary: profile.preferences?.expectedSalary || null,
        noticePeriod: profile.preferences?.noticePeriod || '',
        profileCompletionPercentage: profile.completionPercentage || 0
      },
      // Company details for quick access
      companyDetails: companyDetails,
      // Application metadata
      applicationSource: 'web_portal',
      notes: [],
      interviewScheduled: null,
      feedbackGiven: false
    };

    // Sanitize applicationDoc to remove undefined fields
    Object.keys(applicationDoc).forEach(key => {
      if (applicationDoc[key] === undefined) {
        delete applicationDoc[key];
      }
    });

    // Also sanitize nested objects if necessary
    if (applicationDoc.companyDetails) {
      Object.keys(applicationDoc.companyDetails).forEach(key => {
        if (applicationDoc.companyDetails[key] === undefined) {
          delete applicationDoc.companyDetails[key];
        }
      });
    }

    const docRef = await addDoc(collection(db, COLLECTIONS.APPLICATIONS), applicationDoc);

    console.log('✅ [applyForJob] Application created:', docRef.id);

    // Update subscription usage stats
    try {
      console.log('📊 [applyForJob] Updating application usage stats...');
      const subscriptionResult = await getUserActiveSubscription(applicantId);

      if (subscriptionResult.success && subscriptionResult.subscription) {
        const subscription = subscriptionResult.subscription;
        await updateUsageStats(subscription.id, 'applicationsUsed', 1);
        console.log('✅ [applyForJob] Usage stats updated successfully');
      } else {
        console.warn('⚠️ [applyForJob] No active subscription found, skipping usage update');
      }
    } catch (statsError) {
      console.error('❌ [applyForJob] Error updating usage stats:', statsError);
      // Don't fail the application if stats update fails
    }

    // Send notification to employer
    try {
      console.log('🔔 [applyForJob] Sending notification to employer...');
      await sendPersonalNotification(job.userId, {
        title: 'New Job Application',
        body: `${profile.personalInfo.firstName} ${profile.personalInfo.lastName} applied for ${job.title}`,
        data: {
          type: 'new_application',
          applicationId: docRef.id,
          jobId: jobId,
          jobTitle: job.title,
          applicantName: `${profile.personalInfo.firstName} ${profile.personalInfo.lastName}`
        }
      });
      console.log('✅ [applyForJob] Notification sent to employer');
    } catch (notifError) {
      console.error('❌ [applyForJob] Error sending notification:', notifError);
      // Don't fail the application if notification fails
    }

    return {
      success: true,
      applicationId: docRef.id,
      message: 'Application submitted successfully!'
    };
  } catch (error) {
    console.error('Error applying for job:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Check if user has already applied for a job
 * @param {string} jobId - Job ID
 * @param {string} applicantId - User ID
 * @returns {Object} Result object
 */
export const checkExistingApplication = async (jobId, applicantId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.APPLICATIONS),
      where('jobId', '==', jobId),
      where('applicantId', '==', applicantId)
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      // Check if any non-withdrawn application exists
      const activeApplication = querySnapshot.docs.find(
        doc => doc.data().status !== APPLICATION_STATUS.WITHDRAWN
      );

      if (activeApplication) {
        return {
          exists: true,
          applicationId: activeApplication.id,
          application: { id: activeApplication.id, ...activeApplication.data() }
        };
      }
    }

    return { exists: false };
  } catch (error) {
    console.error('Error checking existing application:', error);
    return { exists: false, error: error.message };
  }
};

/**
 * Get applications for a specific job seeker
 * @param {string} applicantId - User ID
 * @param {Object} options - Query options
 * @returns {Object} Result object with applications
 */
export const getUserApplications = async (applicantId, options = {}) => {
  try {
    // Query without orderBy to avoid index requirements
    const q = query(
      collection(db, COLLECTIONS.APPLICATIONS),
      where('applicantId', '==', applicantId)
    );

    const querySnapshot = await getDocs(q);

    const applications = querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          appliedAt: data.appliedAt ? (
            typeof data.appliedAt.toDate === 'function'
              ? data.appliedAt.toDate()
              : data.appliedAt instanceof Date
                ? data.appliedAt
                : new Date(data.appliedAt)
          ) : null,
          updatedAt: data.updatedAt ? (
            typeof data.updatedAt.toDate === 'function'
              ? data.updatedAt.toDate()
              : data.updatedAt instanceof Date
                ? data.updatedAt
                : new Date(data.updatedAt)
          ) : null,
          interviewScheduled: data.interviewScheduled ? (
            typeof data.interviewScheduled.toDate === 'function'
              ? data.interviewScheduled.toDate()
              : data.interviewScheduled instanceof Date
                ? data.interviewScheduled
                : new Date(data.interviewScheduled)
          ) : null
        };
      })
      // Filter out withdrawn applications
      .filter(app => app.status !== APPLICATION_STATUS.WITHDRAWN);

    // Sort manually in memory (newest first)
    applications.sort((a, b) => {
      const dateA = a.appliedAt || new Date(0);
      const dateB = b.appliedAt || new Date(0);
      return dateB - dateA; // Descending order
    });

    // Apply limit after sorting if specified
    const limitedApplications = options.limit ? applications.slice(0, options.limit) : applications;

    return {
      success: true,
      applications: limitedApplications
    };
  } catch (error) {
    console.error('Error fetching user applications:', error);
    return {
      success: false,
      error: error.message,
      applications: []
    };
  }
};

/**
 * Get applications for a specific job (for employers)
 * @param {string} jobId - Job ID
 * @param {Object} options - Query options
 * @returns {Object} Result object with applications
 */
export const getJobApplications = async (jobId, options = {}) => {
  try {
    // Query without orderBy to avoid index requirements
    let q = query(
      collection(db, COLLECTIONS.APPLICATIONS),
      where('jobId', '==', jobId)
    );

    if (options.status) {
      q = query(
        collection(db, COLLECTIONS.APPLICATIONS),
        where('jobId', '==', jobId),
        where('status', '==', options.status)
      );
    }

    const querySnapshot = await getDocs(q);
    const applications = querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          appliedAt: data.appliedAt ? (
            typeof data.appliedAt.toDate === 'function'
              ? data.appliedAt.toDate()
              : data.appliedAt instanceof Date
                ? data.appliedAt
                : new Date(data.appliedAt)
          ) : null,
          updatedAt: data.updatedAt ? (
            typeof data.updatedAt.toDate === 'function'
              ? data.updatedAt.toDate()
              : data.updatedAt instanceof Date
                ? data.updatedAt
                : new Date(data.updatedAt)
          ) : null,
          interviewScheduled: data.interviewScheduled ? (
            typeof data.interviewScheduled.toDate === 'function'
              ? data.interviewScheduled.toDate()
              : data.interviewScheduled instanceof Date
                ? data.interviewScheduled
                : new Date(data.interviewScheduled)
          ) : null
        };
      })
      // Filter out withdrawn applications
      .filter(app => app.status !== APPLICATION_STATUS.WITHDRAWN);

    // Sort manually in memory (newest first)
    applications.sort((a, b) => {
      const dateA = a.appliedAt || new Date(0);
      const dateB = b.appliedAt || new Date(0);
      return dateB - dateA;
    });

    // Apply limit after sorting if specified
    const limitedApplications = options.limit ? applications.slice(0, options.limit) : applications;

    return {
      success: true,
      applications: limitedApplications
    };
  } catch (error) {
    console.error('Error fetching job applications:', error);
    return {
      success: false,
      error: error.message,
      applications: []
    };
  }
};

/**
 * Get all applications for an employer
 * @param {string} employerId - Employer user ID
 * @param {Object} options - Query options
 * @returns {Object} Result object with applications
 */
export const getEmployerApplications = async (employerId, options = {}) => {
  try {
    console.log('🔍 [ApplicationService] Fetching applications for employerId:', employerId);

    // Query without orderBy to avoid index requirements
    let q = query(
      collection(db, COLLECTIONS.APPLICATIONS),
      where('employerId', '==', employerId)
    );

    // Add status filter if provided
    if (options.status) {
      q = query(
        collection(db, COLLECTIONS.APPLICATIONS),
        where('employerId', '==', employerId),
        where('status', '==', options.status)
      );
    }

    console.log('🔍 [ApplicationService] Executing query without orderBy (will sort in memory)...');
    const querySnapshot = await getDocs(q);
    console.log('✅ [ApplicationService] Query successful, found:', querySnapshot.size, 'applications');

    // If no results found, let's also check if there are any applications at all
    if (querySnapshot.size === 0) {
      console.log('🔍 [ApplicationService] No applications found for employerId, checking all applications...');
      const allAppsQuery = query(collection(db, COLLECTIONS.APPLICATIONS));
      const allAppsSnapshot = await getDocs(allAppsQuery);
      console.log('📊 [ApplicationService] Total applications in database:', allAppsSnapshot.size);

      if (allAppsSnapshot.size > 0) {
        console.log('📋 [ApplicationService] Sample application data:');
        allAppsSnapshot.docs.slice(0, 3).forEach((doc, index) => {
          console.log(`Application ${index + 1}:`, doc.data());
        });
      }
    }

    const applications = querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        console.log('📄 [ApplicationService] Processing application:', doc.id, data);
        return {
          id: doc.id,
          ...data,
          appliedAt: data.appliedAt ? (
            typeof data.appliedAt.toDate === 'function'
              ? data.appliedAt.toDate()
              : data.appliedAt instanceof Date
                ? data.appliedAt
                : new Date(data.appliedAt)
          ) : null,
          updatedAt: data.updatedAt ? (
            typeof data.updatedAt.toDate === 'function'
              ? data.updatedAt.toDate()
              : data.updatedAt instanceof Date
                ? data.updatedAt
                : new Date(data.updatedAt)
          ) : null,
          interviewScheduled: data.interviewScheduled ? (
            typeof data.interviewScheduled.toDate === 'function'
              ? data.interviewScheduled.toDate()
              : data.interviewScheduled instanceof Date
                ? data.interviewScheduled
                : new Date(data.interviewScheduled)
          ) : null
        };
      })
      // Filter out withdrawn applications
      .filter(app => app.status !== APPLICATION_STATUS.WITHDRAWN);

    // Sort manually in memory (newest first)
    applications.sort((a, b) => {
      const dateA = a.appliedAt || new Date(0);
      const dateB = b.appliedAt || new Date(0);
      return dateB - dateA; // Descending order (newest first)
    });

    // Apply limit after sorting if specified
    const limitedApplications = options.limit ? applications.slice(0, options.limit) : applications;

    console.log('✅ [ApplicationService] Returning', limitedApplications.length, 'applications for employer:', employerId);
    return {
      success: true,
      applications: limitedApplications
    };
  } catch (error) {
    console.error('Error fetching employer applications:', error);
    return {
      success: false,
      error: error.message,
      applications: []
    };
  }
};

/**
 * Update application status
 * @param {string} applicationId - Application ID
 * @param {string} newStatus - New status
 * @param {Object} updateData - Additional data to update
 * @returns {Object} Result object
 */
export const updateApplicationStatus = async (applicationId, newStatus, updateData = {}) => {
  try {
    const applicationRef = doc(db, COLLECTIONS.APPLICATIONS, applicationId);
    const existingDoc = await getDoc(applicationRef);

    if (!existingDoc.exists()) {
      return {
        success: false,
        error: 'Application not found'
      };
    }

    const applicationData = existingDoc.data();

    const updateObj = {
      status: newStatus,
      updatedAt: serverTimestamp(),
      ...updateData
    };

    // Add status change to notes
    if (updateData.note) {
      const existingNotes = applicationData.notes || [];
      updateObj.notes = [
        ...existingNotes,
        {
          note: updateData.note,
          status: newStatus,
          timestamp: serverTimestamp(),
          addedBy: updateData.updatedBy || 'system'
        }
      ];
    }

    await updateDoc(applicationRef, updateObj);

    // Send notification to job seeker about status change
    try {
      console.log('🔔 [updateApplicationStatus] Sending notification to seeker...');

      const statusMessages = {
        [APPLICATION_STATUS.REVIEWING]: 'Your application is being reviewed',
        [APPLICATION_STATUS.SHORTLISTED]: 'Congratulations! You have been shortlisted',
        [APPLICATION_STATUS.INTERVIEWED]: 'You have been scheduled for an interview',
        [APPLICATION_STATUS.REJECTED]: 'Your application status has been updated',
        [APPLICATION_STATUS.ACCEPTED]: 'Congratulations! Your application has been accepted'
      };

      const notificationBody = statusMessages[newStatus] || 'Your application status has been updated';

      await sendPersonalNotification(applicationData.applicantId, {
        title: `Application Update: ${applicationData.jobTitle}`,
        body: notificationBody,
        data: {
          type: 'application_status_update',
          applicationId: applicationId,
          jobId: applicationData.jobId,
          jobTitle: applicationData.jobTitle,
          newStatus: newStatus
        }
      });
      console.log('✅ [updateApplicationStatus] Notification sent to seeker');
    } catch (notifError) {
      console.error('❌ [updateApplicationStatus] Error sending notification:', notifError);
      // Don't fail the status update if notification fails
    }

    return {
      success: true,
      message: 'Application status updated successfully'
    };
  } catch (error) {
    console.error('Error updating application status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Withdraw application (for job seekers)
 * @param {string} applicationId - Application ID
 * @param {string} applicantId - Applicant ID for verification
 * @returns {Object} Result object
 */
export const withdrawApplication = async (applicationId, applicantId) => {
  try {
    const applicationRef = doc(db, COLLECTIONS.APPLICATIONS, applicationId);
    const applicationDoc = await getDoc(applicationRef);

    if (!applicationDoc.exists()) {
      return {
        success: false,
        error: 'Application not found'
      };
    }

    const applicationData = applicationDoc.data();

    // Verify ownership
    if (applicationData.applicantId !== applicantId) {
      return {
        success: false,
        error: 'Unauthorized to withdraw this application'
      };
    }

    // Check if application can be withdrawn
    if ([APPLICATION_STATUS.ACCEPTED, APPLICATION_STATUS.REJECTED].includes(applicationData.status)) {
      return {
        success: false,
        error: 'Cannot withdraw application that has been accepted or rejected'
      };
    }

    await updateDoc(applicationRef, {
      status: APPLICATION_STATUS.WITHDRAWN,
      updatedAt: serverTimestamp(),
      withdrawnAt: serverTimestamp()
    });

    return {
      success: true,
      message: 'Application withdrawn successfully'
    };
  } catch (error) {
    console.error('Error withdrawing application:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get application statistics for job seeker
 * @param {string} applicantId - User ID
 * @returns {Object} Result object with stats
 */
export const getApplicationStats = async (applicantId) => {
  try {
    const applications = await getUserApplications(applicantId);

    if (!applications.success) {
      return applications;
    }

    const stats = {
      total: applications.applications.length,
      pending: 0,
      reviewing: 0,
      shortlisted: 0,
      interviewed: 0,
      accepted: 0,
      rejected: 0,
      withdrawn: 0
    };

    applications.applications.forEach(app => {
      stats[app.status] = (stats[app.status] || 0) + 1;
    });

    return {
      success: true,
      stats
    };
  } catch (error) {
    console.error('Error getting application stats:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get application statistics for employer
 * @param {string} employerId - Employer ID
 * @returns {Object} Result object with stats
 */
export const getEmployerApplicationStats = async (employerId) => {
  try {
    const applications = await getEmployerApplications(employerId);

    if (!applications.success) {
      return applications;
    }

    const stats = {
      total: applications.applications.length,
      pending: 0,
      reviewing: 0,
      shortlisted: 0,
      interviewed: 0,
      accepted: 0,
      rejected: 0,
      withdrawn: 0
    };

    applications.applications.forEach(app => {
      stats[app.status] = (stats[app.status] || 0) + 1;
    });

    return {
      success: true,
      stats
    };
  } catch (error) {
    console.error('Error getting employer application stats:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Schedule interview for application
 * @param {string} applicationId - Application ID
 * @param {Date} interviewDate - Interview date and time
 * @param {Object} interviewDetails - Interview details
 * @returns {Object} Result object
 */
export const scheduleInterview = async (applicationId, interviewDate, interviewDetails = {}) => {
  try {
    const applicationRef = doc(db, COLLECTIONS.APPLICATIONS, applicationId);

    await updateDoc(applicationRef, {
      status: APPLICATION_STATUS.INTERVIEWED,
      interviewScheduled: Timestamp.fromDate(interviewDate),
      interviewDetails: {
        date: Timestamp.fromDate(interviewDate),
        location: interviewDetails.location || '',
        type: interviewDetails.type || 'in-person', // 'in-person', 'video', 'phone'
        notes: interviewDetails.notes || '',
        scheduledBy: interviewDetails.scheduledBy || 'employer'
      },
      updatedAt: serverTimestamp()
    });

    return {
      success: true,
      message: 'Interview scheduled successfully'
    };
  } catch (error) {
    console.error('Error scheduling interview:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
